import { FunctionalComponent } from "preact";
import { useState } from "preact/hooks";
import { Bookmark } from "../utils/api";
import { usePrefetch, useIntersectionPrefetch } from "../hooks/usePrefetch";

interface BookmarkListProps {
  bookmarks: Bookmark[];
  loading: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  search?: string;
  onDelete: (id: string) => void;
  onEdit: (bookmark: Bookmark) => void;
  onView: (bookmark: Bookmark) => void;
  onToggleReadLater?: (bookmark: Bookmark) => void;
  onArchive?: (bookmark: Bookmark) => void;
  onLoadMore?: () => void;
}

const BookmarkSkeleton: FunctionalComponent = () => (
  <div class="bg-white dark:bg-stone-900 rounded-xl p-6 border border-stone-200 dark:border-stone-800 animate-pulse shadow-sm">
    <div class="flex items-start gap-4">
      <div class="flex-1 min-w-0">
        <div class="h-6 bg-stone-100 dark:bg-stone-800 rounded w-3/4 mb-3"></div>
        <div class="h-4 bg-stone-100 dark:bg-stone-800 rounded w-1/2 mb-4"></div>
        <div class="h-3 bg-stone-100 dark:bg-stone-800 rounded w-full mb-2"></div>
        <div class="h-3 bg-stone-100 dark:bg-stone-800 rounded w-2/3"></div>
        <div class="flex gap-2 mt-4">
          <div class="h-6 w-16 bg-stone-100 dark:bg-stone-800 rounded-full"></div>
          <div class="h-6 w-20 bg-stone-100 dark:bg-stone-800 rounded-full"></div>
        </div>
      </div>
      <div class="flex gap-1">
        <div class="w-9 h-9 bg-stone-100 dark:bg-stone-800 rounded-full"></div>
        <div class="w-9 h-9 bg-stone-100 dark:bg-stone-800 rounded-full"></div>
      </div>
    </div>
  </div>
);

export const BookmarkList: FunctionalComponent<BookmarkListProps> = ({
  bookmarks,
  loading,
  loadingMore,
  hasMore,
  search,
  onDelete,
  onEdit,
  onView,
  onToggleReadLater,
  onArchive,
  onLoadMore,
}) => {
  const { prefetchOnHover, cancelPrefetch } = usePrefetch(bookmarks, search);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const closeMenu = () => setOpenMenuId(null);

  // Prefetch next page when user scrolls near the bottom
  const prefetchTriggerRef = useIntersectionPrefetch(async () => {
    if (onLoadMore && hasMore && !loadingMore) {
      onLoadMore();
    }
  }, { rootMargin: "400px" });

  if (loading) {
    return (
      <div class="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <BookmarkSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (bookmarks.length === 0) {
    return (
      <div class="text-center py-12">
        <svg
          class="mx-auto h-12 w-12 text-stone-300 dark:text-stone-700"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
          />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-stone-900 dark:text-white">
          No bookmarks
        </h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Get started by adding your first bookmark.
        </p>
      </div>
    );
  }

  return (
    <div class="space-y-6">
      {bookmarks.map((bookmark) => (
          <div
          key={bookmark.id}
          class="group relative bg-white dark:bg-stone-900 rounded-xl p-6 shadow-sm border border-stone-200 dark:border-stone-800 hover:shadow-md hover:border-primary-200 dark:hover:border-primary-900/50 transition-all duration-300 overflow-hidden"
          onMouseEnter={() => prefetchOnHover(bookmark.id)}
          onMouseLeave={cancelPrefetch}
          onClick={() => closeMenu()}
        >
          {/* Bookmark Accent */}
          <div class={`absolute left-0 top-0 bottom-0 w-1.5 transition-colors duration-300 ${bookmark.readLater ? 'bg-amber-400' : 'bg-stone-100 dark:bg-stone-800 group-hover:bg-primary-400'}`} />
          
          <div class="flex items-start gap-2 sm:gap-4">
            <div class="flex items-start gap-2 sm:gap-4 min-w-0 flex-1">
              {/* Favicon */}
              <div class="flex-shrink-0 mt-1.5">
                {bookmark.faviconUrl ? (
                  <img
                    src={bookmark.faviconUrl}
                    alt=""
                    class="w-5 h-5 rounded-sm grayscale-[0.5] group-hover:grayscale-0 transition-all"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <div class="w-5 h-5 flex items-center justify-center text-stone-300 dark:text-stone-600">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </div>
                )}
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1 min-w-0">
                  <a
                    href={bookmark.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-xl font-serif font-semibold text-stone-900 dark:text-stone-50 hover:text-primary-600 dark:hover:text-primary-400 transition-colors leading-tight truncate"
                  >
                    {bookmark.title}
                  </a>
                  {/* Validation Status Badge */}
                  {bookmark.urlValidationStatus === "valid" && (
                    <span class="flex-shrink-0 inline-flex items-center text-green-600 dark:text-green-400" title="Valid URL">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                      </svg>
                    </span>
                  )}
                  {bookmark.urlValidationStatus === "invalid" && (
                    <span class="flex-shrink-0 inline-flex items-center text-red-600 dark:text-red-400" title="Invalid URL">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                      </svg>
                    </span>
                  )}
                </div>
                <a
                  href={bookmark.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-stone-400 dark:text-stone-500 truncate block hover:text-primary-500 transition-colors mb-3"
                >
                  {bookmark.url}
                </a>
                {bookmark.description && (
                  <p class="text-base font-serif text-stone-600 dark:text-stone-300 line-clamp-2 leading-relaxed mb-4">
                    {bookmark.description}
                  </p>
                )}
                {bookmark.tags && (
                  <div class="flex flex-wrap gap-2">
                    {(() => {
                      try {
                        const tags = JSON.parse(bookmark.tags);
                        return Array.isArray(tags) ? tags.map((tag: string) => (
                          <span
                            key={tag}
                            class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 border border-stone-200 dark:border-stone-700 group-hover:border-primary-200 dark:group-hover:border-primary-900/30 transition-colors"
                          >
                            {tag}
                          </span>
                        )) : null;
                      } catch {
                        return null;
                      }
                    })()}
                  </div>
                )}
              </div>
            </div>
            <div class="hidden sm:flex ml-2 sm:ml-4 flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              {onToggleReadLater && (
                <button
                  onClick={() => onToggleReadLater(bookmark)}
                  class={`p-2 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 transition-all ${
                    bookmark.readLater
                      ? "text-amber-500"
                      : "text-stone-400 hover:text-amber-500"
                  }`}
                  title={bookmark.readLater ? "Remove from Read Later" : "Add to Read Later"}
                >
                  <svg class="h-5 w-5" fill={bookmark.readLater ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              )}
              <button
                onClick={() => onView(bookmark)}
                class="p-2 text-stone-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 transition-all"
                title="View Details"
              >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
              {onArchive && (
                <button
                  onClick={() => onArchive(bookmark)}
                  class={`p-2 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 transition-all ${
                    bookmark.isArchived
                      ? "text-primary-500"
                      : "text-stone-400 hover:text-primary-500"
                  }`}
                  title={bookmark.isArchived ? "Read Archived" : "Archive Page"}
                >
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                </button>
              )}
              <button
                onClick={() => onEdit(bookmark)}
                class="p-2 text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 transition-all"
                title="Edit"
              >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                onClick={() => onDelete(bookmark.id)}
                class="p-2 text-stone-400 hover:text-red-600 dark:hover:text-red-400 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 transition-all"
                title="Delete"
              >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
            <div class="relative sm:hidden">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenuId(openMenuId === bookmark.id ? null : bookmark.id);
                }}
                class="p-2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 transition-all"
              >
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                </svg>
              </button>
              {openMenuId === bookmark.id && (
                <div class="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-stone-800 rounded-lg shadow-lg border border-stone-200 dark:border-stone-700 py-1 z-50">
                  {onToggleReadLater && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleReadLater(bookmark);
                        closeMenu();
                      }}
                      class={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                        bookmark.readLater
                          ? "text-amber-600 dark:text-amber-400"
                          : "text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700"
                      }`}
                    >
                      <svg class="h-4 w-4" fill={bookmark.readLater ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {bookmark.readLater ? "Remove from Read Later" : "Read Later"}
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onView(bookmark);
                      closeMenu();
                    }}
                    class="w-full px-4 py-2 text-left text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 flex items-center gap-2"
                  >
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Details
                  </button>
                  {onArchive && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onArchive(bookmark);
                        closeMenu();
                      }}
                      class={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                        bookmark.isArchived
                          ? "text-primary-600 dark:text-primary-400"
                          : "text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700"
                      }`}
                    >
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                      </svg>
                      {bookmark.isArchived ? "Read Archived" : "Archive"}
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(bookmark);
                      closeMenu();
                    }}
                    class="w-full px-4 py-2 text-left text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 flex items-center gap-2"
                  >
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(bookmark.id);
                      closeMenu();
                    }}
                    class="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-stone-100 dark:hover:bg-stone-700 flex items-center gap-2"
                  >
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
          <div class="mt-4 pt-4 border-t border-stone-50 dark:border-stone-800/50 flex items-center gap-4 text-xs text-stone-400 dark:text-stone-500">
            <span class="flex items-center gap-1">
              <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {new Date(bookmark.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
            </span>
            {bookmark.isRead && (
              <span class="flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">
                <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                Read
              </span>
            )}
            {bookmark.readLater && (
              <span class="flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium">
                <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Read later
              </span>
            )}
            {bookmark.isArchived && (
              <span class="flex items-center gap-1 text-primary-600 dark:text-primary-400 font-medium">
                <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                Archived
              </span>
            )}
          </div>
        </div>
      ))}
      {/* Invisible trigger for prefetching next page */}
      <div ref={prefetchTriggerRef} class="h-1" aria-hidden="true" />
      
      {/* Loading indicator for infinite scroll */}
      {loadingMore && (
        <div class="flex justify-center py-8">
          <div class="flex items-center gap-2 text-stone-400 dark:text-stone-500">
            <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span class="text-sm">Loading more...</span>
          </div>
        </div>
      )}
      
      {/* End of list indicator */}
      {!loadingMore && hasMore === false && bookmarks.length > 0 && (
        <div class="text-center py-8 text-stone-400 dark:text-stone-500 text-sm">
          No more bookmarks to load
        </div>
      )}
    </div>
  );
};
